import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { ApiResponse } from '../types'

class ApiService {
  private api: AxiosInstance

  constructor() {
    // Use environment variable for API URL, fallback to relative path for development
    const baseURL = import.meta.env.VITE_API_URL || '/api'
    
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        console.log('API Request Interceptor:', {
          url: config.url,
          method: config.method,
          hasToken: !!token,
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
          currentHeaders: config.headers
        })
        
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
          console.log('Authorization header set:', config.headers.Authorization?.substring(0, 30) + '...')
        } else {
          console.warn('No token found in localStorage for API request')
        }
        return config
      },
      (error) => {
        console.error('Request interceptor error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        console.log('API Response:', {
          url: response.config.url,
          status: response.status,
          success: response.data.success
        })
        return response
      },
      async (error) => {
        console.error('API Error Details:', {
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.response?.data?.error?.message || error.message,
          headers: error.response?.headers
        })
        
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            // Try to refresh the token
            const token = localStorage.getItem('auth_token')
            if (token) {
              console.log('Attempting token refresh...')
              const refreshResponse = await axios.post(`${this.api.defaults.baseURL}/auth/refresh`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              })

              if (refreshResponse.data.success) {
                const newToken = refreshResponse.data.data.token
                localStorage.setItem('auth_token', newToken)
                console.log('Token refreshed successfully')
                
                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.api(originalRequest)
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
            // Only redirect to login if we're not already on the login page
            if (!window.location.pathname.includes('/login')) {
              localStorage.removeItem('auth_token')
              localStorage.removeItem('user')
              window.location.href = '/login'
            }
            return Promise.reject(refreshError)
          }
        }

        // Only redirect to login for 401 errors if we're not already on login page
        // and if the refresh attempt failed
        if (error.response?.status === 401 && originalRequest._retry) {
          console.log('Unauthorized after refresh attempt - redirecting to login')
          if (!window.location.pathname.includes('/login')) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user')
            window.location.href = '/login'
          }
        }
        
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.api.get<ApiResponse<T>>(url, { params })
    return response.data.data!
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(url, data)
    return response.data.data!
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.put<ApiResponse<T>>(url, data)
    return response.data.data!
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.api.delete<ApiResponse<T>>(url)
    return response.data.data!
  }

  // Raw methods for when you need the full response
  async getRaw<T>(url: string, params?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.get<ApiResponse<T>>(url, { params })
  }

  async postRaw<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.post<ApiResponse<T>>(url, data)
  }

  async putRaw<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.put<ApiResponse<T>>(url, data)
  }

  async deleteRaw<T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.delete<ApiResponse<T>>(url)
  }
}

export const apiService = new ApiService()
export default apiService